import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ImageModal from '../components/ImageModal';
import MessageModal from '../components/MessageModal';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const IndividualProfit = ({ user, setView }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [amount, setAmount] = useState('');
    const [transactionType, setTransactionType] = useState('Investment');
    const [investmentType, setInvestmentType] = useState('Cash Deposit');
    const [transactionDate, setTransactionDate] = useState('');
    const [receiptImage, setReceiptImage] = useState(null);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    const [editingTransaction, setEditingTransaction] = useState(null);

    const [totalInvested, setTotalInvested] = useState(0);
    const [totalWithdrawn, setTotalWithdrawn] = useState(0);
    const [myTotalSalesRevenue, setMyTotalSalesRevenue] = useState(0);
    const [myTotalSalesProfit, setMyTotalSalesProfit] = useState(0);

    const currentInvestment = totalInvested - totalWithdrawn;
    const maxWithdrawable = currentInvestment * 0.7;

    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [viewingImageUrl, setViewingImageUrl] = useState('');
    const [confirmation, setConfirmation] = useState({ message: '', onConfirm: null, showCancel: false });

    useEffect(() => {
        if (!user) { setLoading(false); return; }
        setLoading(true);

        const investmentsQuery = query(collection(db, "investments"), where("userId", "==", user.uid), orderBy("date", "desc"));
        const unsubscribeInvestments = onSnapshot(investmentsQuery, (snapshot) => {
            const transData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), date: doc.data().date?.toDate() }));
            let invested = 0;
            let withdrawn = 0;
            transData.forEach(t => {
                if (t.type === 'Investment') {
                    invested += t.amount;
                } else {
                    withdrawn += t.amount;
                }
            });
            setTransactions(transData);
            setTotalInvested(invested);
            setTotalWithdrawn(withdrawn);
        }, (err) => {
            console.error("Error fetching investments:", err);
            setError("Could not load transaction data.");
        });

        const salesQuery = query(collection(db, "sales"), where("founderId", "==", user.uid));
        const unsubscribeSales = onSnapshot(salesQuery, (snapshot) => {
            let totalProfit = 0;
            let totalRevenue = 0;
            snapshot.forEach(doc => {
                const saleData = doc.data();
                totalProfit += saleData.totalProfit || 0;
                totalRevenue += saleData.sellingPrice || 0;
            });
            setMyTotalSalesProfit(totalProfit);
            setMyTotalSalesRevenue(totalRevenue);
        }, (err) => {
            console.error("Error fetching your sales data:", err);
        });

        setLoading(false);
        
        return () => {
            unsubscribeInvestments();
            unsubscribeSales();
        };
    }, [user]);

    useEffect(() => {
        if (editingTransaction) {
            setTransactionType(editingTransaction.type);
            setInvestmentType(editingTransaction.investmentType || 'Cash Deposit');
            setAmount(editingTransaction.amount);
            setTransactionDate(editingTransaction.date ? editingTransaction.date.toISOString().split('T')[0] : '');
            setDescription(editingTransaction.description || '');
            setReceiptImage(null);
            setFormMessage('');
        }
    }, [editingTransaction]);

    const handleReceiptChange = (e) => { if (e.target.files[0]) { setReceiptImage(e.target.files[0]); } };

    const handleTransactionSubmit = async (e) => {
        e.preventDefault();
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount) || numAmount <= 0) { setFormMessage("Please enter a valid, positive amount."); return; }
        if (!transactionDate) { setFormMessage("Please select a transaction date."); return; }
        if (transactionType === 'Investment' && !editingTransaction && !receiptImage) { setFormMessage("Please upload a receipt as proof of investment."); return; }
        if (transactionType === 'Withdrawal' && numAmount > maxWithdrawable) { setFormMessage(`Withdrawal amount cannot exceed 70% of your current investment (Max: RM ${maxWithdrawable.toFixed(2)}).`); return; }
        
        setIsSubmitting(true);
        setFormMessage('Processing transaction...');

        try {
            let receiptImageUrl = editingTransaction ? (editingTransaction.receiptImageUrl || '') : '';
            if (receiptImage) {
                setFormMessage('Uploading receipt...');
                const imageRef = ref(storage, `receipts/${user.uid}/${Date.now()}_${receiptImage.name}`);
                const snapshot = await uploadBytes(imageRef, receiptImage);
                receiptImageUrl = await getDownloadURL(snapshot.ref);
            }

            if (editingTransaction) {
                const transactionRef = doc(db, "investments", editingTransaction.id);
                await updateDoc(transactionRef, {
                    type: transactionType,
                    investmentType: transactionType === 'Investment' ? investmentType : '',
                    amount: numAmount,
                    description,
                    date: new Date(transactionDate),
                    receiptImageUrl: receiptImageUrl,
                    lastUpdatedAt: new Date()
                });
                setFormMessage('Transaction updated successfully!');
                setTimeout(() => resetFormAndState(), 3000);
            } else {
                setFormMessage('Recording transaction...');
                await addDoc(collection(db, "investments"), {
                    userId: user.uid,
                    userName: user.displayName,
                    type: transactionType,
                    investmentType: transactionType === 'Investment' ? investmentType : '',
                    amount: numAmount,
                    description,
                    date: new Date(transactionDate),
                    receiptImageUrl: receiptImageUrl,
                    createdAt: new Date(),
                });
                setFormMessage(`Your ${transactionType} has been recorded successfully!`);
                resetFormAndState(false);
                // ADDED: This will clear the success message after 3 seconds
                setTimeout(() => {
                    setFormMessage('');
                }, 3000);
            }
        } catch (err) {
            console.error("Error saving transaction:", err);
            setFormMessage("Failed to record transaction. Please check the console for details.");
        }
        setIsSubmitting(false);
    };

    const resetFormAndState = (clearMessage = true) => {
        setAmount('');
        setDescription('');
        setReceiptImage(null);
        if (document.getElementById('receipt-upload')) {
            document.getElementById('receipt-upload').value = null;
        }
        setTransactionDate('');
        setEditingTransaction(null);
        if (clearMessage) {
            setFormMessage('');
        }
    };

    const formatDate = (date) => date ? date.toLocaleDateString('en-GB') : 'N/A';
    
    const handleViewReceipt = (url) => {
        setViewingImageUrl(url);
        setIsImageModalOpen(true);
    };

    const handleDeleteTransaction = (transactionId) => {
        if (user.role !== 'super-admin') {
            setConfirmation({ message: "You do not have permission to delete this transaction.", showCancel: false, onConfirm: () => setConfirmation({ message: '' }) });
            return;
        }
        setConfirmation({
            message: "Are you sure you want to permanently delete this transaction record?",
            showCancel: true,
            onConfirm: async () => {
                await deleteDoc(doc(db, "investments", transactionId));
                setConfirmation({ message: '' });
            },
            onCancel: () => setConfirmation({ message: '' })
        });
    };

    if (loading) return <p>Loading your investment data...</p>;
    if (error) return <p style={{color: 'var(--brand-danger)'}}>{error}</p>;

    return (
        <div className="individual-profit-container">
            {isImageModalOpen && <ImageModal imageUrl={viewingImageUrl} onClose={() => setIsImageModalOpen(false)} />}
            <MessageModal {...confirmation} onCancel={() => setConfirmation({ message: '' })} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Individual Investment & Profit</h2>
                <button type="button" className="cancel-button" style={{padding: '0.5rem 1rem'}} onClick={() => setView('dashboard')}>Back to Dashboard</button>
            </div>

            <div className="investment-grid">
                <div className="form-section">
                    <h3>{editingTransaction ? 'Edit Transaction' : 'New Transaction'}</h3>
                    <form onSubmit={handleTransactionSubmit} className="investment-form">
                        <div className="form-field"><label>Transaction Type</label><select value={transactionType} onChange={e => setTransactionType(e.target.value)}><option value="Investment">Add Investment</option><option value="Withdrawal">Make Withdrawal</option></select></div>
                        {transactionType === 'Investment' && (<div className="form-field"><label>Investment Type</label><select value={investmentType} onChange={e => setInvestmentType(e.target.value)}><option value="Cash Deposit">Cash Deposit</option><option value="Product Purchase">Product Purchase</option></select></div>)}
                        <div className="form-field"><label>Amount (RM)</label><input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g., 5000.00" required/></div>
                        <div className="form-field">
                            <label>Transaction Date</label>
                            <DatePicker
                                selected={transactionDate ? new Date(transactionDate) : null}
                                onChange={(date) => setTransactionDate(date.toISOString().split('T')[0])}
                                dateFormat="yyyy-MM-dd"
                                className="date-picker-input"
                                placeholderText="Select a date"
                                required
                            />
                        </div>
                        {transactionType === 'Investment' && (<div className="form-field"><label>Proof of Investment (Receipt)</label><input id="receipt-upload" type="file" onChange={handleReceiptChange} accept="image/*" required={!editingTransaction} /></div>)}
                        <div className="form-field"><label>Description (Optional)</label><input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g., Initial Capital"/></div>
                        
                        <div className="form-actions">
                            <button type="submit" className="submit-button" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : (editingTransaction ? 'Update Transaction' : 'Record Transaction')}</button>
                            {editingTransaction ? (
                                <button type="button" className="cancel-button" onClick={resetFormAndState}>Cancel Edit</button>
                            ) : (
                                <button type="button" className="cancel-button" onClick={() => setView('dashboard')}>Cancel</button>
                            )}
                        </div>
                        {formMessage && <div className="popup-message">{formMessage}</div>}
                    </form>
                </div>

                <div className="metric-card">
                    <h4 className="metric-title">Current Investment Balance</h4>
                    <p className="metric-value">RM {currentInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="metric-footer">Total Invested - Total Withdrawn</p>
                </div>
                <div className="metric-card">
                    <h4 className="metric-title">Max. Withdrawable Amount (70%)</h4>
                    <p className="metric-value" style={{color: 'var(--brand-warning)'}}>RM {maxWithdrawable.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="metric-footer">30% must remain as investment</p>
                </div>
                <div className="metric-card">
                    <h4 className="metric-title">My Sales Performance</h4>
                    <div className="spec-field" style={{padding: '0.5rem', marginBottom: '1rem'}}>
                        <strong>Total Revenue From Your Sales</strong>
                        <div>RM {myTotalSalesRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                     <div className="spec-field" style={{padding: '0.5rem'}}>
                        <strong>Total Profit From Your Sales</strong>
                        <div style={{color: 'var(--brand-success)', fontWeight: 'bold'}}>RM {myTotalSalesProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                </div>

                <div className="form-section full-span">
                    <h3>Your Transaction History</h3>
                    <div className="table-wrapper">
                        <table className="product-list-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                    <th>Proof</th>
                                    <th className="cell-right">Amount (RM)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.length === 0 ? (
                                    <tr><td colSpan="6" style={{textAlign: 'center'}}>No transactions recorded yet.</td></tr>
                                ) : (
                                    transactions.map(t => (
                                        <tr key={t.id}>
                                            <td>{formatDate(t.date)}</td>
                                            <td><span className={`transaction-type-badge type-${t.type}`}>{t.type}</span>{t.type === 'Investment' && <div className="investment-type-label">{t.investmentType}</div>}</td>
                                            <td>{t.description || '-'}</td>
                                            <td>{t.receiptImageUrl ? (<button className="product-id-link" onClick={() => handleViewReceipt(t.receiptImageUrl)}>View</button>) : ('N/A')}</td>
                                            <td className="cell-right" style={{color: t.type === 'Withdrawal' ? 'var(--brand-danger)' : 'var(--brand-success)'}}>{t.type === 'Withdrawal' ? '-' : '+'} {t.amount.toFixed(2)}</td>
                                            <td className="cell-actions">
                                                <button className="edit-button" onClick={() => setEditingTransaction(t)}>Edit</button>
                                                <button className="delete-button" onClick={() => handleDeleteTransaction(t.id)}>Delete</button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndividualProfit;